import {defineStore} from "pinia";
import axios from "axios";
import {handleError, useInitializeStore} from "./storeHelper";
import {BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {computed, reactive, ref} from "vue";
import {
    BFieldSelection,
    BFilterableField,
    BFilterOptions,
    decimalOperations,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import type {Vendor, VendorOri} from "@/types";
import {serializeFilters} from "@/components/BTable/HelperFunctions";
import {PagedList} from "@/audit.types";
import {useGlobalMessagesStore} from "./globalMessagesStore";

export const useVendorStore = defineStore("vendorStore", () => {
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = "api/v1/vendor/";
    const loading = ref<boolean>(false);
    const items = ref<Vendor[]>();
    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                isDescending: false,
                key: "name",
            },
        ],
        filterBy: [],
        pagingMode: "withTotal",
    } as BTableOptions);

    const roles = ref<BFieldSelection[]>();
    const filterableFields = ref<BFilterableField[]>();

    useInitializeStore(async () => {
        filterableFields.value = reactive<BFilterableField[]>([
            {
                property: "name",
                label: "Vendor Name ",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "agencyCount",
                label: "Number of Agencies",
                operations: decimalOperations,
                editorComponent: "text-editor",
            },
        ]);
    });

    const exportFile = async (fileType: string) => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get(`${apiUrl}export`, {
                responseType: "blob",
                params: {
                    fileType,
                    sort: sortParam,
                    filter: filterParam,
                },
            });

            const blob = new Blob([response.data], {type: response.headers["content-type"]});

            return blob;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const list = async () => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<Vendor>>(apiUrl, {
                params: {
                    pageSize: tableOptions.value.itemsPerPage,
                    page: tableOptions.value.page,
                    sort: sortParam,
                    filter: filterParam,
                },
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const create = async (vendor: Vendor): Promise<void> => {
        try {
            loading.value = true;
            await axios.put(`${apiUrl}`, vendor);
            globalMessages.push({message: "Vendor created successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const update = async (vendorCode: string, vendor: Vendor): Promise<void> => {
        try {
            loading.value = true;
            await axios.post(`${apiUrl}${vendorCode}`, vendor);
            globalMessages.push({message: "Vendor updated successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteItem = async (vendorCode: string): Promise<void> => {
        try {
            loading.value = true;
            await axios.delete(`${apiUrl}${vendorCode}`);
            globalMessages.push({message: "Vendor deleted successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getById = async (vendorCode: string): Promise<Vendor> => {
        try {
            loading.value = true;
            const response = await axios.get<Vendor>(`${apiUrl}${vendorCode}`);
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const checkIfVendorNameExists = async(name: string): Promise<boolean> => {
        try {
            loading.value = true;
            const response = await axios.get<boolean>(`${apiUrl}${encodeURIComponent(name)}/check-name`);
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    }
    return {
        list,
        getById,
        deleteItem,
        create,
        update,
        exportFile,        
        items,
        filterableFields,
        filterOptions,
        tableOptions,
        roles,
        loading,
        checkIfVendorNameExists
    };
});
